import React, { useCallback, useEffect, useRef, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputSwitch } from 'primereact/inputswitch';
import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';
import alertaService from '../../services/Alerta/AlertaService';

const VACIA = {
    id: null, usuarioId: null, tipoEventoId: null, empresaId: null,
    canal: 'CORREO', requiereConfirmacion: false, enCopia: false,
    esRespaldo: false, soloConsulta: false, activo: true
};

const CANALES = [
    { label: 'Correo', value: 'CORREO' },
    { label: 'En línea', value: 'LINEA' },
    { label: 'Ambos', value: 'AMBOS' }
];

/**
 * Administracion de suscripciones (Bloque E).
 *
 * Es la pantalla que hace que agregar o quitar a alguien de las alertas sea una
 * fila y no un deploy: antes, cambiar un destinatario obligaba a tocar el fuente
 * de NotificacionService, recompilar y redesplegar el backend.
 */
const AdminSuscripciones = () => {

    const toast = useRef(null);

    const [filas, setFilas] = useState([]);
    const [catalogos, setCatalogos] = useState({ usuarios: [], tiposEvento: [], empresas: [] });
    const [cargando, setCargando] = useState(false);
    const [edicion, setEdicion] = useState(null);
    const [guardando, setGuardando] = useState(false);
    const [salud, setSalud] = useState([]);

    const cargar = useCallback(async () => {
        setCargando(true);
        try {
            const [lista, cat, sal] = await Promise.all([
                alertaService.listarSuscripciones(),
                alertaService.catalogosSuscripciones(),
                alertaService.saludSuscripciones()
            ]);
            setFilas(Array.isArray(lista) ? lista : []);
            setCatalogos(cat || { usuarios: [], tiposEvento: [], empresas: [] });
            setSalud(Array.isArray(sal) ? sal : []);
        } catch (e) {
            toast.current?.show({
                severity: 'error', summary: 'No se pudo cargar', life: 4000
            });
        } finally {
            setCargando(false);
        }
    }, []);

    useEffect(() => { cargar(); }, [cargar]);

    const guardar = async () => {
        setGuardando(true);
        try {
            const res = await alertaService.guardarSuscripcion(edicion);
            if (res?.ok) {
                toast.current?.show({ severity: 'success', summary: 'Guardado', life: 2500 });
                setEdicion(null);
                cargar();
            } else {
                // El backend valida y explica por que no se puede. Se muestra tal
                // cual: es informacion util, no un error generico.
                toast.current?.show({
                    severity: 'warn', summary: 'No se guardó',
                    detail: res?.mensaje || 'Revisá los datos.', life: 6000
                });
            }
        } catch (e) {
            toast.current?.show({ severity: 'error', summary: 'Error al guardar', life: 4000 });
        } finally {
            setGuardando(false);
        }
    };

    const pedirDesactivar = (f) => {
        confirmDialog({
            message: `¿Desactivar la suscripción de ${f.username}? Dejará de recibir esas alertas.`,
            header: 'Confirmar', icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Desactivar', rejectLabel: 'Cancelar',
            accept: async () => {
                try {
                    const res = await alertaService.desactivarSuscripcion(f.id);
                    if (res?.ok) {
                        toast.current?.show({ severity: 'success', summary: 'Desactivada', life: 2500 });
                        cargar();
                    } else {
                        toast.current?.show({
                            severity: 'warn', summary: 'No se desactivó',
                            detail: res?.mensaje, life: 7000
                        });
                    }
                } catch (e) {
                    toast.current?.show({ severity: 'error', summary: 'Error', life: 4000 });
                }
            }
        });
    };

    const colRol = (f) => {
        if (f.esRespaldo) return <Tag value="RESPALDO" severity="danger" />;
        // Una fila de solo consulta no entrega nada: conviene que se distinga de
        // un destinatario real de un vistazo.
        if (f.soloConsulta) return <Tag value="Solo consulta" severity="warning" />;
        return <Tag value={f.enCopia ? 'Copia' : 'Para'} severity={f.enCopia ? 'info' : 'success'} />;
    };

    const colAcciones = (f) => (
        <div className="flex gap-2">
            <Button icon="pi pi-pencil" className="p-button-text p-button-sm"
                    tooltip="Editar" onClick={() => setEdicion({ ...f })} />
            {f.activo && (
                <Button icon="pi pi-ban" className="p-button-text p-button-sm p-button-danger"
                        tooltip="Desactivar" onClick={() => pedirDesactivar(f)} />
            )}
        </div>
    );

    const encabezado = (
        <div className="flex flex-wrap justify-content-between align-items-center gap-3">
            <span className="text-xl font-bold">Suscripciones a alertas</span>
            <div className="flex gap-2">
                <Button icon="pi pi-refresh" className="p-button-text" onClick={cargar} tooltip="Actualizar" />
                <Button label="Nueva" icon="pi pi-plus" onClick={() => setEdicion({ ...VACIA })} />
            </div>
        </div>
    );

    const esRespaldo = !!edicion?.esRespaldo;

    // Quien se puede estar perdiendo alertas. Vacio es lo normal.
    const enRiesgo = salud.filter((x) => x.control && x.control !== 'ok');

    return (
        <div className="grid">
            <Toast ref={toast} />
            <ConfirmDialog />

            {enRiesgo.length > 0 && (
                <div className="col-12">
                    <div className="card border-left-3 border-red-500">
                        <div className="font-bold text-red-600 mb-2">
                            <i className="pi pi-exclamation-triangle mr-2"></i>
                            Hay {enRiesgo.length} {enRiesgo.length === 1 ? 'persona' : 'personas'} que
                            puede{enRiesgo.length === 1 ? '' : 'n'} no estar viendo sus alertas
                        </div>
                        <DataTable value={enRiesgo} responsiveLayout="scroll" dataKey="username">
                            <Column field="username" header="Usuario" />
                            <Column field="canal" header="Canal" />
                            <Column field="recibidas7d" header="Recibidas (7d)" />
                            <Column field="leidas7d" header="Leídas (7d)" />
                            <Column field="control" header="Motivo"
                                    body={(x) => <Tag value={x.control} severity="danger" />} />
                        </DataTable>
                        <small className="text-color-secondary">
                            Si alguien quedó en canal <b>En línea</b> y no está leyendo, devolvelo a
                            <b> Ambos</b> y averiguá qué pasó antes de volver a apagarle el correo.
                        </small>
                    </div>
                </div>
            )}

            <div className="col-12">
                <div className="card">
                    <p className="text-sm text-color-secondary mt-0">
                        Define quién recibe qué alerta, de qué empresa de transporte y por qué canal.
                        Dejar el tipo o la empresa en <b>(todos)</b> suscribe a todo.
                        La fila marcada <b>RESPALDO</b> recibe únicamente lo que no coincide con ninguna otra.
                    </p>

                    <DataTable value={filas} header={encabezado} loading={cargando}
                               paginator rows={20} dataKey="id" responsiveLayout="scroll"
                               emptyMessage="No hay suscripciones cargadas."
                               rowClassName={(f) => (!f.activo ? 'text-color-secondary' : '')}>
                        <Column field="username" header="Usuario" sortable style={{ minWidth: '10rem' }} />
                        <Column field="nombreUsuario" header="Nombre" sortable style={{ minWidth: '12rem' }} />
                        <Column field="correo" header="Correo" style={{ minWidth: '14rem' }} />
                        <Column header="Tipo de alerta" style={{ minWidth: '10rem' }}
                                body={(f) => f.tipoEvento || <i className="text-color-secondary">(todos)</i>} />
                        <Column header="Transporte" style={{ minWidth: '10rem' }}
                                body={(f) => f.empresa || <i className="text-color-secondary">(todas)</i>} />
                        <Column field="canal" header="Canal" sortable style={{ minWidth: '7rem' }} />
                        <Column header="Rol" body={colRol} style={{ minWidth: '8rem' }} />
                        <Column header="Confirma" style={{ minWidth: '7rem' }}
                                body={(f) => (f.requiereConfirmacion
                                    ? <Tag value="Sí" severity="warning" /> : '—')} />
                        <Column header="Estado" style={{ minWidth: '7rem' }}
                                body={(f) => (f.activo
                                    ? <Tag value="Activa" severity="success" />
                                    : <Tag value="Inactiva" />)} />
                        <Column header="" body={colAcciones} style={{ minWidth: '7rem' }} />
                    </DataTable>
                </div>
            </div>

            <Dialog visible={!!edicion} onHide={() => setEdicion(null)}
                    header={edicion?.id ? 'Editar suscripción' : 'Nueva suscripción'}
                    style={{ width: '34rem', maxWidth: '96vw' }}>
                {edicion && (
                    <div className="grid formgrid p-fluid">
                        <div className="field col-12">
                            <label>Usuario</label>
                            <Dropdown value={edicion.usuarioId} filter showClear
                                      options={catalogos.usuarios}
                                      optionValue="usuarioId"
                                      optionLabel="username"
                                      itemTemplate={(u) => (
                                          <div>
                                              <div>{u.username} — {u.nombre}</div>
                                              <small className="text-color-secondary">{u.correo}</small>
                                          </div>
                                      )}
                                      onChange={(e) => setEdicion({ ...edicion, usuarioId: e.value })}
                                      placeholder="Elegí un usuario" />
                            <small className="text-color-secondary">
                                Solo aparecen usuarios activos con correo cargado.
                            </small>
                        </div>

                        <div className="field col-12">
                            <label>Recibe el respaldo</label>
                            <div className="flex align-items-center gap-2 mt-1">
                                <InputSwitch checked={edicion.esRespaldo}
                                             onChange={(e) => setEdicion({
                                                 ...edicion, esRespaldo: e.value,
                                                 // El respaldo no se acota: si se marca, se limpian
                                                 // tipo y empresa para que no quede un estado que
                                                 // el backend va a rechazar igual.
                                                 tipoEventoId: e.value ? null : edicion.tipoEventoId,
                                                 empresaId: e.value ? null : edicion.empresaId,
                                                 enCopia: e.value ? false : edicion.enCopia
                                             })} />
                                <small className="text-color-secondary">
                                    Recibe únicamente las alertas que no coinciden con ninguna otra suscripción.
                                </small>
                            </div>
                        </div>

                        <div className="field col-12 md:col-6">
                            <label>Tipo de alerta</label>
                            <Dropdown value={edicion.tipoEventoId} showClear disabled={esRespaldo}
                                      options={catalogos.tiposEvento} optionValue="id" optionLabel="nombre"
                                      onChange={(e) => setEdicion({ ...edicion, tipoEventoId: e.value })}
                                      placeholder="(todos)" />
                        </div>

                        <div className="field col-12 md:col-6">
                            <label>Empresa de transporte</label>
                            <Dropdown value={edicion.empresaId} showClear filter disabled={esRespaldo}
                                      options={catalogos.empresas} optionValue="id" optionLabel="nombre"
                                      onChange={(e) => setEdicion({ ...edicion, empresaId: e.value })}
                                      placeholder="(todas)" />
                        </div>

                        <div className="field col-12 md:col-6">
                            <label>Canal</label>
                            <Dropdown value={edicion.canal} options={CANALES}
                                      onChange={(e) => setEdicion({ ...edicion, canal: e.value })} />
                        </div>

                        <div className="field col-12 md:col-6">
                            <label>Va en copia (CC)</label>
                            <div className="mt-1">
                                <InputSwitch checked={edicion.enCopia} disabled={esRespaldo}
                                             onChange={(e) => setEdicion({ ...edicion, enCopia: e.value })} />
                            </div>
                        </div>

                        <div className="field col-12">
                            <label>Solo consulta</label>
                            <div className="mt-1 flex align-items-center gap-2">
                                <InputSwitch checked={!!edicion.soloConsulta} disabled={esRespaldo}
                                             onChange={(e) => setEdicion({
                                                 ...edicion, soloConsulta: e.value })} />
                                <small className="text-color-secondary">
                                    Le da alcance para <b>buscar</b> estas alertas, pero no se las
                                    entrega: ni correo ni campanita. Para quien necesita revisar si
                                    una unidad tuvo novedad sin recibirlas todas.
                                </small>
                            </div>
                        </div>

                        <div className="field col-12 md:col-6">
                            <label>Debe marcar “Revisado”</label>
                            <div className="mt-1">
                                <InputSwitch checked={edicion.requiereConfirmacion}
                                             onChange={(e) => setEdicion({
                                                 ...edicion, requiereConfirmacion: e.value })} />
                            </div>
                        </div>

                        <div className="field col-12 md:col-6">
                            <label>Activa</label>
                            <div className="mt-1">
                                <InputSwitch checked={edicion.activo}
                                             onChange={(e) => setEdicion({ ...edicion, activo: e.value })} />
                            </div>
                        </div>

                        <div className="col-12 flex justify-content-end gap-2 mt-3">
                            <Button label="Cancelar" className="p-button-text"
                                    onClick={() => setEdicion(null)} />
                            <Button label="Guardar" icon="pi pi-check" loading={guardando}
                                    onClick={guardar} />
                        </div>
                    </div>
                )}
            </Dialog>
        </div>
    );
};

export default AdminSuscripciones;
